import { CommonModule } from "@angular/common";
import { computed, DestroyRef, ElementRef, inject, input, signal } from "@angular/core";
import { toObservable } from '@angular/core/rxjs-interop';
import { DocumentationGetDocRequest, DocumentationGetTreeRequest } from '@common/documentation/shared';
import { UiButtonComponent } from '@common/ui/frontend-shared/button';
import { UiLoadingIndicatorComponent } from '@common/ui/frontend-shared/loading-indicator';
import { UiTreeComponent } from '@common/ui/frontend-shared/tree';
import { AppMessage, AppRequestService } from '@core/frontend-shared';
import { InnerHtmlClickHandlerDirective, SafeHtmlPipe, takeWhileAlive, UrlType } from '@core/frontend-shared/utils';
import { getAllParentElements } from '@core/frontend-shared/utils/dom';
import { catchError, combineLatest, filter, map, of, startWith, Subject, switchMap, take } from 'rxjs';
import { HighlightJsService } from '../highlightjs';
import { AccordionHelper } from './accordion-helper';
import { DOC_START_ID, TocTree } from './toc-tree';
import { VirtualDocContents, VirtualDocName } from './virtual-docs';
import { TreeBuilder } from '@core/shared/tree';
import * as i0 from "@angular/core";
import * as i1 from "@common/ui/frontend-shared/tree";
import * as i2 from "@angular/common";
const _c0 = ["contentWrapper"];
const _c1 = a0 => ({
  "with-toc": a0
});
const _c2 = a0 => ({
  "collapse-sections": a0
});
function DocumentationViewerComponent_Conditional_15_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "ui-loading-indicator");
  }
}
function DocumentationViewerComponent_Conditional_19_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 16);
    i0.ɵɵlistener("click", function DocumentationViewerComponent_Conditional_19_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onClickToggleToc());
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    i0.ɵɵproperty("ngClass", "ui-button-primary");
  }
}
function DocumentationViewerComponent_Conditional_20_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 15)(1, "div", 17)(2, "div", 18)(3, "h3", 19);
    i0.ɵɵtext(4, "Artikelinhalt");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "button", 20);
    i0.ɵɵlistener("click", function DocumentationViewerComponent_Conditional_20_Template_button_click_5_listener() {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onClickToggleToc());
    });
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(6, "ui-tree", 21);
    i0.ɵɵlistener("selectionChange", function DocumentationViewerComponent_Conditional_20_Template_ui_tree_selectionChange_6_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onSelectTocTreeNode($event));
    });
    i0.ɵɵelementEnd()()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(6);
    i0.ɵɵproperty("value", ctx_r1.tocMenuItems())("static", true);
  }
}
const TREE_NODE_AUTOEXPAND_PATHS = ['/manual', '/development', '/development/libs'];
export let DocumentationViewerComponent = /*#__PURE__*/(() => {
  class DocumentationViewerComponent {
    constructor() {
      this.destroyRef = inject(DestroyRef);
      // only used to pass in external path requests.
      this.documentPath = input(null, ...(ngDevMode ? [{
        debugName: "documentPath"
      }] : /* istanbul ignore next */[]));
      this.noContextHelpFound = input(false, ...(ngDevMode ? [{
        debugName: "noContextHelpFound"
      }] : /* istanbul ignore next */[]));
      this.historyStack = signal([], ...(ngDevMode ? [{
        debugName: "historyStack"
      }] : /* istanbul ignore next */[]));
      this.historyIndex = signal(null, ...(ngDevMode ? [{
        debugName: "historyIndex"
      }] : /* istanbul ignore next */[]));
      this.documentHtmlContent = signal('', ...(ngDevMode ? [{
        debugName: "documentHtmlContent"
      }] : /* istanbul ignore next */[]));
      this.loading = signal(false, ...(ngDevMode ? [{
        debugName: "loading"
      }] : /* istanbul ignore next */[]));
      this.collapseSections = signal(true, ...(ngDevMode ? [{
        debugName: "collapseSections"
      }] : /* istanbul ignore next */[]));
      this.showToc = signal(true, ...(ngDevMode ? [{
        debugName: "showToc"
      }] : /* istanbul ignore next */[]));
      this.tocMenuItems = signal(null, ...(ngDevMode ? [{
        debugName: "tocMenuItems"
      }] : /* istanbul ignore next */[]));
      this.treeSelection = signal(null, ...(ngDevMode ? [{
        debugName: "treeSelection"
      }] : /* istanbul ignore next */[]));
      this.tree = signal(null, ...(ngDevMode ? [{
        debugName: "tree"
      }] : /* istanbul ignore next */[]));
      this.docTreeEntriesAsTreeNodes = computed(() => {
        const tree = this.tree();
        if (!tree) return [];
        return TreeBuilder.transformToPrimeNgTree(tree, e => {
          return {
            label: e.name,
            data: e.path,
            selectable: e.payload.type === 'f',
            icon: e.payload.type === 'f' ? 'file' : 'folder',
            styleClass: e.payload.type === 'f' ? 'node-file' : 'node-folder',
            expandedIcon: e.payload.type === 'd' ? 'folder-open' : undefined,
            expanded: TREE_NODE_AUTOEXPAND_PATHS.includes(e.path)
          };
        });
      }, ...(ngDevMode ? [{
        debugName: "docTreeEntriesAsTreeNodes"
      }] : /* istanbul ignore next */[]));
      this.api = inject(AppRequestService);
      this.hljs = inject(HighlightJsService);
      this.viewInitialized$ = new Subject();
      this.innerHtmlClickHandlerConfig = {
        handler: (info, el, e, defaultHandler) => this.innerHTMLClick(info, el, e, defaultHandler),
        interactibleClasses: [AccordionHelper.cssClasses.Toggle]
      };
      this.loadDocTree();
      this.setupObservables();
    }
    ngAfterViewInit() {
      this.viewInitialized$.next();
    }
    setupObservables() {
      // propagate doc path originating from component input to history stack
      toObservable(this.documentPath).pipe(takeWhileAlive(this.destroyRef), startWith(this.documentPath()), filter(path => !!path)).subscribe(inputPath => {
        this.updateHistoryStack(inputPath);
      });
      // react to document history stack changes
      // could be improved by caching already loaded documents
      const docData$ = combineLatest({
        stack: toObservable(this.historyStack),
        index: toObservable(this.historyIndex)
      }).pipe(takeWhileAlive(this.destroyRef), map(({
        stack,
        index
      }) => {
        if (stack && stack.length > index) return stack[index];else return null;
      }), filter(path => !!path), switchMap(href => {
        if (Object.values(VirtualDocName).includes(href)) {
          this.tocMenuItems.set(null);
          return of({
            content: VirtualDocContents[href],
            docPath: href
          });
        } else {
          let docPath = href;
          let anchor = null;
          if (docPath.indexOf('#') > -1) {
            [docPath, anchor] = docPath.split('#');
          }
          this.expandTree(docPath, true);
          this.tocMenuItems.set(null);
          this.loading.set(true);
          return this.api.sendRequest(DocumentationGetDocRequest, {
            docPath
          }).pipe(map(response => {
            return {
              docPath: docPath,
              content: response.content,
              anchor
            };
          }), catchError(err => {
            if (err instanceof AppMessage) {
              err.setHandled();
              return of({
                content: "<h2>Fehler " + err.status + "</h2><p>" + err.message + "</p>",
                docPath: 'error'
              });
            } else {
              return of({
                content: "<h2>Fehler</h2><p>" + err.toString() + "</p>",
                docPath: 'error'
              });
            }
          }));
        }
      }));
      this.viewInitialized$.pipe(take(1), switchMap(() => docData$)).subscribe(info => {
        this.documentHtmlContent.set(info.content);
        this.loading.set(false);
        setTimeout(() => {
          this.rebuildToc();
          const isAnyChangelog = info.docPath.includes('changelog');
          AccordionHelper.buildSections(this.contentWrapper.nativeElement, isAnyChangelog ? 2 : 3);
          this.applyDocumentContentInteraction();
        }, 1);
        if (info.anchor) {
          setTimeout(() => {
            this.scrollToDocAnchor(info.anchor);
          }, 100);
        }
      });
    }
    // called any time a new document is loaded, allowing to bind / interact with its HTML.
    applyDocumentContentInteraction() {
      const content = this.contentWrapper.nativeElement;
      const codeElements = content.querySelectorAll('code');
      codeElements.forEach(el => {
        const isCodeBlock = el.parentElement.tagName === 'PRE';
        if (isCodeBlock) {
          this.hljs.highlightElement(el);
        } else {
          // inline code - tricking hljs to believe all of these contain shell code, which allows for a kinda even, basic highlighting.
          el.classList.add('language-sh');
          this.hljs.highlightElement(el);
        }
      });
    }
    loadDocTree() {
      this.api.sendRequest(DocumentationGetTreeRequest, {}).subscribe(response => {
        this.tree.set(response.tree);
      });
    }
    rebuildToc() {
      const contentRootEl = this.contentWrapper?.nativeElement;
      if (!contentRootEl) throw new Error('rebuildToc failed: contentWrapper el not available yet');
      const headlinesInDoc = Array.from(contentRootEl.querySelectorAll('h1,h2,h3,h4,h5,h6'));
      const tocTree = TocTree.buildTree(headlinesInDoc);
      let tocTreeNodes = TocTree.toTreeNodes(tocTree);
      if (tocTreeNodes.length > 0) {
        tocTreeNodes = TocTree.prependIntroductionTreeNode(tocTreeNodes);
        this.tocMenuItems.set(tocTreeNodes);
      } else {
        this.tocMenuItems.set(null);
      }
    }
    getCurrentPath() {
      return this.historyStack()[this.historyIndex()];
    }
    navigateWithinDocs(newDocPath) {
      const isRootPath = newDocPath.startsWith('/');
      let solvedPath;
      if (isRootPath) {
        solvedPath = this.solveRootPathLink(newDocPath);
      } else {
        const dummyToTrickUrl = 'http://localhost/';
        // relative
        const resolvedWithBaseUrl = new URL(newDocPath, dummyToTrickUrl + this.getCurrentPath()).href;
        solvedPath = resolvedWithBaseUrl.replace(dummyToTrickUrl, '');
      }
      this.updateHistoryStack(solvedPath);
    }
    // root-base links need to be normalized in some ways, 
    // because there are differences between documentation structure in repository and production/doc viewer.
    solveRootPathLink(newDocPath) {
      let solvedPath;
      if (newDocPath.startsWith('/docs')) {
        // root paths inside doc files will start with /docs as they are located in the docs directory inside cwd.
        // in doc viewer + doc tree, documentation lives in / directory. So trim the path accordingly.
        solvedPath = newDocPath.substring(5);
      } else if (newDocPath.startsWith('/libs')) {
        // technical library docs are stored in the library root dirs in repository, but inside /development/libs in production/doc viewer
        solvedPath = '/development' + newDocPath;
      } else if (newDocPath.startsWith('/tools')) {
        // technical tool docs are stored in the library root dirs in repository, but inside /development/tools in production/doc viewer
        solvedPath = '/development' + newDocPath;
      } else {
        solvedPath = newDocPath;
      }
      return solvedPath;
    }
    updateHistoryStack(solvedPath) {
      const currentIndex = this.historyIndex();
      const currentStack = this.historyStack();
      const newIndex = typeof currentIndex === 'number' ? currentIndex + 1 : 0;
      const newStack = currentStack.length ? currentStack.slice(0, newIndex) : [];
      newStack.push(solvedPath);
      this.historyIndex.set(newIndex);
      this.historyStack.set(newStack);
    }
    // TODO: #86c4wnx61 - disfunctional. Fix when Tree supports correct view updates
    expandTree(solvedPath, selectMatchedNode) {
      if (!this.treeRef) return;
      this.treeRef.updateTree(() => {
        const nodes = this.treeRef.getPathToMatchingTreeNode(n => {
          // cant strict-match because slight variations between links and file paths are allowed
          // e.g. leading slash or not, file extension is optional
          return n.data && n.data.includes(solvedPath);
        });
        if (nodes) {
          nodes.forEach(n => n.expanded = true);
          if (selectMatchedNode) this.treeSelection.set(nodes[nodes.length - 1]);
        }
      });
    }
    clickHistoryGoBack() {
      this.historyIndex.update(i => {
        if (i > 0) return i - 1;
        return 0;
      });
    }
    clickHistoryGoForward() {
      this.historyIndex.update(i => {
        const length = this.historyStack().length;
        if (i < length - 1) return i + 1;
        return i;
      });
    }
    innerHTMLClick(urlInfo, el, e, defaultHandler) {
      if (el.classList.contains(AccordionHelper.cssClasses.Toggle)) {
        // section toggle
        e.preventDefault();
        AccordionHelper.toggleSection(el);
      } else {
        // only used in changelogs
        const taskIdAttr = el.attributes.getNamedItem('data-taskId');
        if (taskIdAttr) {
          e.preventDefault();
          const taskId = taskIdAttr.value;
          const isLegacy = taskId.startsWith('#');
          if (isLegacy) {
            const url = 'https://app.clickup.com/t/' + taskId.substring(1);
            window.open(url, '_blank');
          } else {
            const url = 'https://liveo-mind.atlassian.net/browse/' + taskId;
            window.open(url, '_blank');
          }
          return;
        } else {
          if (urlInfo.type === UrlType.Hash) {
            e.preventDefault();
            this.scrollToDocAnchor(urlInfo.rawUrl);
          } else if (urlInfo.type === UrlType.Relative) {
            e.preventDefault();
            this.navigateWithinDocs(urlInfo.rawUrl);
          } else {
            defaultHandler();
          }
        }
      }
    }
    onSelectTreeNode(node) {
      this.treeSelection.set(node);
      this.navigateWithinDocs(node.data);
    }
    onSelectTocTreeNode(node) {
      if (!node) return; // fix for bad behavior in ui-tree: clicking a node twice will deselect and emit a null arg. Fix in #86c4wnx61
      this.scrollToDocAnchor(node.data);
    }
    onClickToggleToc() {
      this.showToc.set(!this.showToc());
    }
    onClickToggleCollapsedSections() {
      this.collapseSections.set(!this.collapseSections());
    }
    scrollToDocAnchor(elementId) {
      const wrapper = this.contentWrapper.nativeElement;
      let targetElement;
      if (elementId === DOC_START_ID) {
        targetElement = wrapper.querySelector("h1");
      } else {
        if (!elementId.startsWith('#')) elementId = '#' + elementId;
        targetElement = wrapper.querySelector(elementId);
      }
      if (targetElement) {
        // check element and parents for collapsed sections + expand if necessary
        const contentParents = getAllParentElements(targetElement, this.contentWrapper.nativeElement);
        const contentParentsAndSelf = [targetElement, ...contentParents];
        const sections = contentParentsAndSelf.filter(el => {
          return el.classList.contains(AccordionHelper.cssClasses.Section) || el.classList.contains(AccordionHelper.cssClasses.Toggle);
        });
        sections.forEach(section => {
          AccordionHelper.toggleSection(section, true);
        });
        targetElement.scrollIntoView({
          behavior: 'auto'
        });
        targetElement.classList.add('flash');
        setTimeout(() => {
          targetElement.classList.remove('flash');
        }, 500);
      }
    }
    static {
      this.ɵfac = function DocumentationViewerComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DocumentationViewerComponent)();
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: DocumentationViewerComponent,
        selectors: [["documentation-viewer"]],
        viewQuery: function DocumentationViewerComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 5)(UiTreeComponent, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.contentWrapper = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.treeRef = _t.first);
          }
        },
        inputs: {
          documentPath: [1, "documentPath"],
          noContextHelpFound: [1, "noContextHelpFound"]
        },
        decls: 21,
        vars: 20,
        consts: [["contentWrapper", ""], [1, "content-wrapper"], [1, "menubar"], ["selectionMode", "single", 3, "selectionChange", "value", "selection"], [1, "annotation"], [1, "content", 3, "ngClass"], [1, "topbar"], [1, "left"], ["uiButton", "", "icon", "chevron-left", "title", "Vorherige Seite anzeigen", 3, "click", "disabled"], ["uiButton", "", "icon", "chevron-right", "title", "Vorw\u00E4rts in Historie navigieren", 3, "click", "disabled"], ["uiButton", "", 1, "ui-button-grey", 3, "click", "icon", "label", "title"], [1, "right"], [1, "section-main"], [1, "content-container", 3, "innerHTML", "innerHtmlClickHandler", "ngClass"], ["uiButton", "", "icon", "chevron-left", "title", "Artikelnavigation anzeigen", 1, "btn-toggle-toc", 3, "ngClass"], [1, "toc"], ["uiButton", "", "icon", "chevron-left", "title", "Artikelnavigation anzeigen", 1, "btn-toggle-toc", 3, "click", "ngClass"], [1, "toc-sticky"], [1, "toc-header", "ui-margin-bottom"], [1, "ui-margin-remove"], ["uiButton", "", "icon", "thumbtack", "title", "Artikelnavigation ausblenden", 1, "ui-button-invisible", 3, "click"], ["theme", "text", "selectionMode", "single", 3, "selectionChange", "value", "static"]],
        template: function DocumentationViewerComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 1)(1, "div", 2)(2, "ui-tree", 3);
            i0.ɵɵlistener("selectionChange", function DocumentationViewerComponent_Template_ui_tree_selectionChange_2_listener($event) {
              return ctx.onSelectTreeNode($event);
            });
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(3, "div", 4)(4, "small");
            i0.ɵɵtext(5, " Hinweis: Der Dokument-Browser funktioniert aktuell nur eingeschr\u00E4nkt. Dies wird im Rahmen eines sp\u00E4teren umfassenderen UI-Updates behoben. TODO: #86c4wnx61 ");
            i0.ɵɵelementEnd()()();
            i0.ɵɵelementStart(6, "div", 5)(7, "div", 6)(8, "span", 7)(9, "button", 8);
            i0.ɵɵlistener("click", function DocumentationViewerComponent_Template_button_click_9_listener() {
              return ctx.clickHistoryGoBack();
            });
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(10, "button", 9);
            i0.ɵɵlistener("click", function DocumentationViewerComponent_Template_button_click_10_listener() {
              return ctx.clickHistoryGoForward();
            });
            i0.ɵɵelementEnd();
            i0.ɵɵtext(11, " \u00A0\u00A0 ");
            i0.ɵɵelementStart(12, "button", 10);
            i0.ɵɵlistener("click", function DocumentationViewerComponent_Template_button_click_12_listener() {
              return ctx.onClickToggleCollapsedSections();
            });
            i0.ɵɵelementEnd()();
            i0.ɵɵelement(13, "span", 11);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(14, "div", 12);
            i0.ɵɵconditionalCreate(15, DocumentationViewerComponent_Conditional_15_Template, 1, 0, "ui-loading-indicator");
            i0.ɵɵelement(16, "div", 13, 0);
            i0.ɵɵpipe(18, "safeHtml");
            i0.ɵɵelementEnd();
            i0.ɵɵconditionalCreate(19, DocumentationViewerComponent_Conditional_19_Template, 1, 1, "button", 14);
            i0.ɵɵconditionalCreate(20, DocumentationViewerComponent_Conditional_20_Template, 7, 2, "div", 15);
            i0.ɵɵelementEnd()();
          }
          if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("value", ctx.docTreeEntriesAsTreeNodes())("selection", ctx.treeSelection());
            i0.ɵɵadvance(4);
            i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(16, _c1, ctx.showToc()));
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("disabled", ctx.historyIndex() < 1);
            i0.ɵɵadvance();
            i0.ɵɵproperty("disabled", ctx.historyIndex() >= ctx.historyStack().length - 1);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("icon", ctx.collapseSections() ? "bars" : "bars")("label", ctx.collapseSections() ? "Kompakte Ansicht" : "Volle Ansicht")("title", ctx.collapseSections() ? "Wechsel zu Anzeige des gesamten Inhaltes" : "Wechsel zu Ansicht mit ausklappbaren Abschnitten");
            i0.ɵɵadvance(3);
            i0.ɵɵconditional(ctx.loading() ? 15 : -1);
            i0.ɵɵadvance();
            i0.ɵɵproperty("innerHTML", i0.ɵɵpipeBind1(18, 14, ctx.documentHtmlContent() || ""), i0.ɵɵsanitizeHtml)("innerHtmlClickHandler", ctx.innerHtmlClickHandlerConfig)("ngClass", i0.ɵɵpureFunction1(18, _c2, ctx.collapseSections()));
            i0.ɵɵadvance(3);
            i0.ɵɵconditional(!ctx.showToc() ? 19 : -1);
            i0.ɵɵadvance();
            i0.ɵɵconditional(ctx.showToc() && ctx.tocMenuItems() ? 20 : -1);
          }
        },
        dependencies: [i1.UiTreeComponent, CommonModule, i2.NgClass, UiButtonComponent, UiLoadingIndicatorComponent, InnerHtmlClickHandlerDirective, SafeHtmlPipe],
        styles: [".content-wrapper[_ngcontent-%COMP%]{display:flex;height:100%}.menubar[_ngcontent-%COMP%]{width:35%;min-width:350px;max-width:350px;overflow:hidden;display:flex;flex-direction:column;background:var(--backgroundSoftColor)}.menubar[_ngcontent-%COMP%]     ui-tree{flex:1;overflow-y:auto}.menubar[_ngcontent-%COMP%]     ui-tree .p-tree-container{overflow:hidden}.menubar[_ngcontent-%COMP%]     ui-tree .node-folder>.p-treenode-content>ui-icon{color:var(--textSoftColor)!important}.menubar[_ngcontent-%COMP%]     ui-tree .node-file>.p-treenode-content>ui-icon{color:var(--primaryColor)!important}.annotation[_ngcontent-%COMP%]{padding:10px;background:#cb32324d}.content[_ngcontent-%COMP%]{flex:1;display:flex;flex-direction:column;position:relative;overflow:hidden}.content[_ngcontent-%COMP%]   .topbar[_ngcontent-%COMP%]{position:sticky;top:0;height:50px;min-height:50px;padding:0 20px;display:flex;justify-content:space-between;align-items:center;background:var(--contentColor);border-bottom:1px solid var(--softGreyColor)}.content[_ngcontent-%COMP%]   .topbar[_ngcontent-%COMP%] > .left[_ngcontent-%COMP%], .content[_ngcontent-%COMP%]   .topbar[_ngcontent-%COMP%] > .right[_ngcontent-%COMP%]{display:flex;align-items:center}.content[_ngcontent-%COMP%]   .section-main[_ngcontent-%COMP%]{overflow:auto;position:relative;padding:20px}.content[_ngcontent-%COMP%]   .btn-toggle-toc[_ngcontent-%COMP%]{position:absolute;top:60px;right:20px}.content.with-toc[_ngcontent-%COMP%]   .toc[_ngcontent-%COMP%]{width:280px;min-width:280px}.content.with-toc[_ngcontent-%COMP%]   .toc[_ngcontent-%COMP%]{position:absolute;top:50px;padding-top:20px;bottom:0;right:20px}.content.with-toc[_ngcontent-%COMP%]   .toc[_ngcontent-%COMP%]   .toc-header[_ngcontent-%COMP%]{display:flex;align-items:center}.content.with-toc[_ngcontent-%COMP%]   .toc[_ngcontent-%COMP%]   .toc-header[_ngcontent-%COMP%] > *[_ngcontent-%COMP%] + *[_ngcontent-%COMP%]{margin-left:10px}.content.with-toc[_ngcontent-%COMP%]   .toc[_ngcontent-%COMP%]   .ui-button-invisible[_ngcontent-%COMP%]:hover{background:var(--primaryColor);color:var(--primaryTextColor)}.content.with-toc[_ngcontent-%COMP%]   .toc[_ngcontent-%COMP%]   .toc-sticky[_ngcontent-%COMP%]{position:sticky;top:0}.content.with-toc[_ngcontent-%COMP%]   .section-main[_ngcontent-%COMP%]{padding-right:305px;scrollbar-gutter:stable}.content-container[_ngcontent-%COMP%]     *{scroll-margin-top:70px}.content-container[_ngcontent-%COMP%]     h1, .content-container[_ngcontent-%COMP%]     h2, .content-container[_ngcontent-%COMP%]     h3, .content-container[_ngcontent-%COMP%]     h4, .content-container[_ngcontent-%COMP%]     h5, .content-container[_ngcontent-%COMP%]     h6{transition:color 1s}.content-container[_ngcontent-%COMP%]     .flash{color:var(--highlightColor);transition:color .1s}.content-container[_ngcontent-%COMP%]     pre{background:var(--backgroundSoftColor);padding:15px;overflow-x:auto;font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace}.content-container[_ngcontent-%COMP%]     .ui-simpletable{--tableHeaderCellBgColor:var(--secondaryColor)}.content-container.collapse-sections[_ngcontent-%COMP%]     .section-collapsible.collapsed{display:none}.content-container.collapse-sections[_ngcontent-%COMP%]     .section-collapsible.expanded{background:var(--backgroundSofterColor);margin-left:-10px;padding:10px}.content-container.collapse-sections[_ngcontent-%COMP%]     .section-toggle{background:var(--backgroundColor);display:block;padding:5px 10px;margin-left:-10px;margin-bottom:0;margin-top:15px}.content-container.collapse-sections[_ngcontent-%COMP%]     .section-toggle:hover{background:var(--primaryColor);color:var(--primaryTextColor);cursor:pointer}"],
        changeDetection: 0
      });
    }
  }
  return DocumentationViewerComponent;
})();